/**
 * One surface across successive periods — the "how is this shifting" view,
 * rendered when a Usage row is expanded.
 *
 * Fetched per expanded row rather than for every row up front: a trend query is
 * one per surface, and a table of 100 surfaces would be 100 queries to show
 * sparklines nobody asked to see.
 *
 * See docs/FeatureDesigns/user-activity-analytics.md.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchWithAdmin } from '../../api/fetchHelper';
import type { RollupPeriod, TrendResponse } from './types';
import { trendFrom } from './periods';
import './UserActions.css';

export interface SurfaceTrendProps {
  period: RollupPeriod;
  kind: string;
  surface: string;
  tenantId?: string;
}

export function SurfaceTrend({ period, kind, surface, tenantId }: SurfaceTrendProps) {
  const trend = useQuery({
    queryKey: ['user-activity-trend', tenantId, period, kind, surface],
    queryFn: () => {
      const p = new URLSearchParams({ period, kind, surface, from: trendFrom(period, new Date()) });
      if (tenantId) p.set('tenantId', tenantId);
      return fetchWithAdmin<TrendResponse>(`/console/user-activity-usage/trend?${p}`);
    },
  });

  if (trend.isLoading) return <p className="ua-muted ua-trend-msg">Loading trend…</p>;
  if (trend.isError) return <p className="ua-error ua-trend-msg">Failed: {(trend.error as Error).message}</p>;

  const points = trend.data?.points ?? [];
  if (points.length === 0) {
    return <p className="ua-muted ua-trend-msg">No earlier periods recorded for this surface yet.</p>;
  }

  // Bars are scaled against the series max, so the shape reads even when the
  // absolute numbers are small.
  const peak = Math.max(...points.map(p => p.events));

  return (
    <div className="ua-trend">
      <div className="ua-trend-head">
        <span className="ua-trend-title">Events per {period}</span>
        <span className="ua-dim">peak {peak.toLocaleString()}</span>
      </div>
      <ol className="ua-trend-bars">
        {points.map(p => (
          <li key={p.periodStart} className="ua-trend-row">
            <span className="ua-trend-label">{p.periodStart}</span>
            <span className="ua-trend-track">
              <span className="ua-trend-bar" style={{ width: `${peak ? (p.events / peak) * 100 : 0}%` }} />
            </span>
            <span className="ua-trend-value">
              {p.events.toLocaleString()}
              <span className="ua-dim"> · {p.users.toLocaleString()} users</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
